import { useSelector } from 'react-redux';

import { useNavigate } from 'hooks/useNavigate';

import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { RoutePath } from 'enums/Routes';

import { getApplicationData } from 'selectors/getApplicationData';

import { goalIsPersonalFundsOnly } from 'handlers/yourGoal';
import { GetApplicationData, HardOfferData } from 'handlers/applicationData';
import { addDays } from 'utils/dateUtils';

import { getMaxExpectedPayoffDate, getPayoffLabel } from './upcomingSectionUtils';

export enum VisualType {
  Emoji = 'emoji',
  Logo = 'logo',
  Icon = 'icon',
  None = 'none',
}

export enum IconName {
  Done = 'done',
  Pending = 'pending',
  Transfer = 'transfer',
  Settings = 'settings',
  Assignment = 'assignment',
}

export type UpcomingItemVisual =
  | { type: VisualType.Emoji; value: string }
  | { type: VisualType.Logo; url: string }
  | { type: VisualType.Icon; name: IconName };

export interface UpcomingItem {
  title: string;
  description?: string;
  value?: number | string;
  visual?: UpcomingItemVisual;
  date?: Date;
  groupLabel?: string;
  onClick?: () => void;
}

export interface UpcomingGroup {
  label: string;
  items: UpcomingItem[];
}

export interface UpcomingData {
  groups: UpcomingGroup[];
}

export interface UpcomingDataItems {
  items: UpcomingItem[];
}

interface StepItem {
  appStatus: ApplicationStatusName;
  title: string;
  onClick?: () => void;
}

const FUNDED_STATUSES: ApplicationStatusName[] = [ApplicationStatusName.Funded, ApplicationStatusName.AwaitingFunding];

const HIDE_PAYOFF_ITEM_STATUSES = [
  ApplicationStatusName.Rejected,
  ApplicationStatusName.OfferAvailable,
] as ApplicationStatusName[];
const buildPayoffItem = (application: GetApplicationData): UpcomingItem | null => {
  const { status, loanGoal, loanAmount, hardOffer, bankName, accountMask, dateOfLoan } = application;

  if (HIDE_PAYOFF_ITEM_STATUSES.includes(status)) return null;

  const isPersonalExpenseOnly = goalIsPersonalFundsOnly(loanGoal);
  const isFunded = FUNDED_STATUSES.includes(status);
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);

  const fiveDaysAfterdateOfLoan = getMaxExpectedPayoffDate(dateOfLoan);

  if (!hardOffer || fiveDaysAfterdateOfLoan < startOfDay) return null;

  let value: number | undefined;

  if (isFunded) {
    const { payOff, keepIt } = hardOffer as HardOfferData;
    value =
      [...payOff, ...keepIt]
        .filter((t) => !t.weWillPay)
        .reduce((acc, tradeline) => acc + tradeline.balanceToConsolidate, 0) +
      (hardOffer as HardOfferData).additionalFundsTotal?.amount;
  } else {
    value = loanAmount;
  }

  if (!value) return null;

  let title: string;
  if (isFunded) {
    title = `${bankName} ${accountMask}`;
  } else if (isPersonalExpenseOnly) {
    title = 'Get funds deposited in your account';
  } else {
    title = 'Pay off your debts';
  }

  const description = isFunded ? 'ACH' : 'If approved';

  return {
    title,
    description,
    value,
    visual: {
      type: VisualType.Emoji,
      value: '🎉',
    },
    date: addDays(new Date(), 5),
    groupLabel: getPayoffLabel(dateOfLoan),
  };
};

export const useUpcomingData = (): UpcomingItem[] => {
  const navigate = useNavigate();
  const applicationData = useSelector(getApplicationData);
  const applicationStatus = applicationData.application?.status;

  const buildTodayItems = (): UpcomingItem[] => {
    const allSteps: StepItem[] = [
      {
        appStatus: ApplicationStatusName.Verification,
        title: 'Verify your information',
        onClick: () => navigate(RoutePath.VerificationSteps),
      },
      {
        appStatus: ApplicationStatusName.FinalizeLoan,
        title: 'Choose debts to pay off',
        onClick: () => navigate(RoutePath.SelectDebts),
      },
      {
        appStatus: ApplicationStatusName.SetUpDeduction,
        title: 'Set up repayment',
        onClick: () => navigate(RoutePath.RepaymentMethod),
      },
      {
        appStatus: ApplicationStatusName.SigningAgreement,
        title: 'Sign loan agreement',
        onClick: () => navigate(RoutePath.DocuSignSignature),
      },
    ];

    const currentStep = allSteps.find((step) => step.appStatus === applicationStatus);

    if (!currentStep) return [];

    return [
      {
        title: currentStep.title,
        visual: {
          type: VisualType.Icon,
          name: IconName.Pending,
        },
        date: new Date(),
        groupLabel: 'Today',
        onClick: currentStep.onClick,
      },
    ];
  };

  const buildExpectedPayoffItems = (): UpcomingItem[] => {
    const { application } = applicationData;
    if (!application) return [];

    const payoffItem = buildPayoffItem(application);
    if (!payoffItem) return [];

    return [payoffItem];
  };

  const todayItems = buildTodayItems();
  const expectedPayoffItems = buildExpectedPayoffItems();

  return [...todayItems, ...expectedPayoffItems];
};
